import { GetTokenApi } from './GetTokenApi';
import type {
    TokenRequestParams,
    ConsumerTokenRequestParams,
    EnterpriseTokenRequestParams,
} from 'owa-tokenprovider-utils';
import { ScenarioType } from 'owa-tokenprovider-utils';
import { LogStartPerformanceUsage } from 'owa-analytics-start';
import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import { NUM_OF_RETRIES, RETRY_DELAY } from './const/constants';
import { fetchTokenFromHostAppForEnterpriseWithRetry } from './utils/fetchTokenFromHostAppForEnterprise';
import { fetchTokenFromHostAppForConsumerWithRetry } from './utils/fetchTokenFromHostAppForConsumer';
import { isBootFeatureEnabled } from 'owa-metatags';
import type { ITokenQueue } from './utils/ITokenQueue';
import type { ITokenCache } from './utils/ITokenCache';
import { type UnifiedTokenCache } from 'auth-token-cache';
import type { MailboxInfo } from 'owa-client-types';
import { isAccountFeatureEnabled } from 'owa-feature-flags';

const APIS_ALLOWED_FOR_REFRESHING = ['BizchatSSRToken'];

function isConsumerTokenRequest(params: TokenRequestParams): params is ConsumerTokenRequestParams {
    return params.scenarioType === ScenarioType.Consumer;
}

export class GetTokenApiUsingHostApp extends GetTokenApi {
    public readonly LoggerPrefix = 'GetTokenApiUsingHostApp';
    private hostApp: string;

    constructor(
        queue: ITokenQueue,
        cache: ITokenCache,
        unifiedCache: UnifiedTokenCache,
        hostApp?: string
    ) {
        super(queue, cache, unifiedCache);
        this.hostApp = hostApp ?? 'UNKNOWN';
    }

    protected override createDatapoint(
        mailboxInfo: MailboxInfo | undefined
    ): LogStartPerformanceUsage {
        let perfLog: LogStartPerformanceUsage;
        if (this.hostApp === 'native') {
            perfLog = new LogStartPerformanceUsage('GetTokenApiUsingHostApp_Native', mailboxInfo);
        } else if (this.hostApp === 'outlookdesktop') {
            perfLog = new LogStartPerformanceUsage(
                'GetTokenApiUsingHostApp_OutlookDesktop',
                mailboxInfo
            );
        } else if (this.hostApp === 'macoutlook') {
            perfLog = new LogStartPerformanceUsage(
                'GetTokenApiUsingHostApp_MacOutlook',
                mailboxInfo
            );
        } else if (this.hostApp === 'teams') {
            perfLog = new LogStartPerformanceUsage('GetTokenApiUsingHostApp_Teams', mailboxInfo);
        } else {
            perfLog = new LogStartPerformanceUsage('GetTokenApiUsingHostApp', mailboxInfo);
        }
        perfLog.addCustomProperty('hostApp', this.hostApp);
        return perfLog;
    }

    private shouldUseWarmTokenCallback(requestParams: TokenRequestParams): boolean {
        return (
            // Don't want to do this for OPX or hosted app other flows
            this.hostApp === 'native' &&
            requestParams.mailboxInfo !== undefined &&
            requestParams.apiName !== undefined &&
            (isAccountFeatureEnabled(
                'mon-copilot-bizchat-ssr',
                requestParams.mailboxInfo,
                true /*SSR flows should be after boot, when this should be initialized*/
            ) ||
                isAccountFeatureEnabled(
                    'mon-copilot-bizchat-fullscreen-ssr',
                    requestParams.mailboxInfo,
                    true /*SSR flows should be after boot, when this should be initialized*/
                )) &&
            APIS_ALLOWED_FOR_REFRESHING.includes(requestParams.apiName)
        );
    }

    private async getKeepTokensWarmCallback(
        requestParams: TokenRequestParams,
        perfLog: LogStartPerformanceUsage
    ): Promise<TokenResponse | undefined> {
        if (isConsumerTokenRequest(requestParams)) {
            return this.getTokenForConsumerCallback(requestParams, perfLog);
        }

        return this.getTokenForEnterpriseCallback(requestParams, perfLog);
    }

    public async getTokenForEnterpriseCallback(
        params: TokenRequestParams,
        perfLog: LogStartPerformanceUsage
    ): Promise<TokenResponse> {
        const enterpriseParams = params as EnterpriseTokenRequestParams;

        let cacheKey: string = '';
        if (isBootFeatureEnabled('auth-unifiedCache')) {
            if (enterpriseParams.mailboxInfo) {
                cacheKey = this.unifiedCache.getCacheKey(
                    enterpriseParams.mailboxInfo,
                    enterpriseParams.resource,
                    enterpriseParams.scope,
                    enterpriseParams.targetTenantId,
                    undefined /* childAppId */
                );
            }
        } else {
            cacheKey = this.tokenCache.getCacheKey(enterpriseParams);
        }

        perfLog.addCustomProperty(`${this.LoggerPrefix}EnterpriseCallback`, true);
        const tokenResponse = await fetchTokenFromHostAppForEnterpriseWithRetry(
            perfLog,
            NUM_OF_RETRIES,
            RETRY_DELAY,
            enterpriseParams
        );

        let enterpriseTokenCallback: (() => Promise<TokenResponse | undefined>) | undefined;
        if (this.shouldUseWarmTokenCallback(enterpriseParams)) {
            enterpriseTokenCallback = () =>
                this.getKeepTokensWarmCallback(enterpriseParams, perfLog);
        }

        if (isBootFeatureEnabled('auth-unifiedCache')) {
            if (enterpriseParams.mailboxInfo) {
                this.unifiedCache.putCachedToken(
                    cacheKey,
                    true,
                    tokenResponse,
                    enterpriseTokenCallback
                );
            }
        } else {
            this.tokenCache.putCachedToken(
                cacheKey,
                enterpriseParams,
                tokenResponse,
                enterpriseTokenCallback
            );
        }

        return tokenResponse;
    }

    public async getTokenForConsumerCallback(
        params: TokenRequestParams,
        perfLog: LogStartPerformanceUsage
    ): Promise<TokenResponse> {
        const consumerParams = params as ConsumerTokenRequestParams;

        let cacheKey: string = '';
        if (isBootFeatureEnabled('auth-unifiedCache')) {
            if (consumerParams.mailboxInfo) {
                cacheKey = this.unifiedCache.getCacheKey(
                    consumerParams.mailboxInfo,
                    consumerParams.resource,
                    consumerParams.scope,
                    consumerParams.targetTenantId,
                    undefined /* childAppId */
                );
            }
        } else {
            cacheKey = this.tokenCache.getCacheKey(consumerParams);
        }

        perfLog.addCustomProperty(`${this.LoggerPrefix}ConsumerCallback`, true);
        const tokenResponse = await fetchTokenFromHostAppForConsumerWithRetry(
            perfLog,
            NUM_OF_RETRIES,
            RETRY_DELAY,
            consumerParams
        );

        let consumerTokenCallback: (() => Promise<TokenResponse | undefined>) | undefined;
        if (this.shouldUseWarmTokenCallback(consumerParams)) {
            consumerTokenCallback = () => this.getKeepTokensWarmCallback(consumerParams, perfLog);
        }

        if (isBootFeatureEnabled('auth-unifiedCache')) {
            if (consumerParams.mailboxInfo) {
                this.unifiedCache.putCachedToken(
                    cacheKey,
                    false,
                    tokenResponse,
                    consumerTokenCallback
                );
            }
        } else {
            this.tokenCache.putCachedToken(
                cacheKey,
                consumerParams,
                tokenResponse,
                consumerTokenCallback
            );
        }

        return tokenResponse;
    }
}
